import { useAtomValue } from 'jotai';
import * as m from 'motion/react-m';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { webinarBookedOutAtom } from '@/atoms/demoAtoms';
import { type MeetingBookerHandle } from '@/container/demo-flow/meeting-booker/meeting-booker.types';
import { useIsOnboardingComplianceCopyEnabled } from '@/features/flags';
import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { useSelectedSpecialtyCanonicalName } from '@/features/onboarding-v2/hooks/useSelectedSpecialtyCanonicalName';
import { isRequestDemoBlocked } from '@/features/onboarding-v2/utils/isRequestDemoBlocked';
import { useQuerySpecialtyOnboardingV2Assets } from '@/hooks/query/useQuerySpecialtyOnboardingV2Assets';
import { useOnboardingExperience } from '@/utils/onboardingExperience';
import { Button } from '@heidi/design-system/button';
import { TypographyH3, TypographyP } from '@heidi/design-system/typography';

import { useGuidedTourExposure } from '@/features/onboarding-v2/hooks/useGuidedTourExposure';
import { useIsMicTestAutofillEnabled } from '@/features/onboarding-v2/hooks/useIsMicTestAutofillEnabled';
import { useLocalizedSpecialtyName } from '@/features/onboarding-v2/hooks/useLocalizedSpecialtyName';

import { LearnBasicsSteppedStep } from './LearnBasicsSteppedStep';
import { getActiveEditTasks } from './NoteEditStep.utils';
import { PersonalizationLayout } from './PersonalizationLayout';
import { DemoOptInSection } from './shared/DemoOptInSection';
import {
  AboutYouStep,
  ClinicStep,
  InviteTeamStep,
  MicTestStep,
  PrivacyStep,
} from './steps';
import { MicTestStepPlaySample } from './steps/MicTestStepPlaySample';
import { GuidedTourSteps } from './tour/GuidedTourSteps';
import { isGuidedTourStep } from './tour/guidedTourSteps.utils';
import { TourTranscriptionStep } from './tour/TourTranscriptionStep';

export function LearnBasicsFlowSteps() {
  const intl = useIntl();
  const showComplianceCopy = useIsOnboardingComplianceCopyEnabled();
  const ctx = useOnboardingContext();
  const {
    formData,
    updateFormData,
    currentStep,
    currentStepIndex,
    isCurrentStepValid,
    availableDisplayLanguages,
    showLanguageDropdown,
    handleCountryChange,
    handleContinue,
    analytics,
    goToNextStep,
    setSkipOriginStep,
    micTest,
    completion,
    isGuidedTour,
    isLearnBasicsPreviewFlow,
  } = ctx;

  // mic-test is the last step both arms share; everything after it differs, so
  // this is where the user genuinely sees their variant. Firing here (rather
  // than on flag evaluation) keeps the Amplitude denominator to users who
  // reached the divergence — and excludes sub-`lg` viewports entirely, which
  // the guided-tour guards already drop out of the experiment.
  useGuidedTourExposure({
    isGuidedTour,
    isAtTourEntryPoint: isLearnBasicsPreviewFlow && currentStep === 'mic-test',
  });

  // Onboarding-v2 specialty assets feed every preview surface in this flow:
  // here for the mic-test transcript, and (via the same query key, dedup'd)
  // inside MicTestPreview for the abbreviation/evidence/document panels.
  // Use the canonical (English) specialty name for the asset lookup — the BE
  // resolves by English name, so the localized picker value must not be sent.
  const specialtyAssetName = useSelectedSpecialtyCanonicalName();
  const { data: onboardingAssets } = useQuerySpecialtyOnboardingV2Assets(
    specialtyAssetName,
    intl.locale
  );
  const micSampleText = onboardingAssets?.transcript ?? '';
  const localizedSpecialty = useLocalizedSpecialtyName();

  // Gate the redesigned auto-fill mic-test step against the previous "play a
  // sample" design (off → play-sample control).
  const micTestAutofillEnabled = useIsMicTestAutofillEnabled();
  const MicTestStepComponent = micTestAutofillEnabled
    ? MicTestStep
    : MicTestStepPlaySample;

  // Shared across both mic-test designs so the guided tour changes only the
  // screen, never what the step writes.
  const handleMicTestContinue = () => {
    micTest.setTranscript(micSampleText);
    micTest.setHasCompleted(true);
    micTest.setPhase('generating');
    handleContinue();
  };
  const handleMicTestSkip = () => {
    analytics.trackStepSkipped('mic-test');
    micTest.setTranscript(micSampleText);
    micTest.setHasCompleted(true);
    micTest.setPhase('finished');
    goToNextStep();
  };

  const handleNoteEditContinue = () => {
    const completed = getActiveEditTasks(intl.locale, { includeDetailLevelTask: true })
      .filter((task) => micTest.editActivity[task.key])
      .map((task) => task.key);
    analytics.trackLearnBasicsCompleted(completed);
    // Genuine completion: clear any skip origin so the privacy step does not
    // show the skip-recovery Back button (and a re-traversal after going back
    // doesn't leave a stale origin).
    setSkipOriginStep(null);
    handleContinue();
  };
  const handleNoteEditSkip = () => {
    // GRO-3157: standardise per-step skips on `onboarding_step_skipped` so
    // funnels can compare skip rates across mic-test and note-edit using
    // `step_name`, forwarding `tasks_completed_count` for depth-of-engagement.
    const tasksCompletedCount = getActiveEditTasks(intl.locale, {
      includeDetailLevelTask: true,
    }).filter((task) => micTest.editActivity[task.key]).length;
    analytics.trackStepSkipped('note-edit', { tasksCompletedCount });
    goToNextStep();
  };

  const meetingBookerRef = useRef<MeetingBookerHandle>(null);

  // Country-config drives the demo decision (CTA selector +
  // meeting/discovery-call URLs). The impression event fires from
  // `DemoOptInSection` itself when the clinic step renders it, so the variant
  // reported is the one the user actually sees — not whatever the default
  // resolves to before they pick a country on the about-you step.
  const experience = useOnboardingExperience({
    countryCode: formData.country?.countryCode,
    countryName: formData.country?.name,
    languageCode: formData.displayLanguage,
  });

  const handleClinicContinue = () => {
    // Defer the actual HubSpot POST until the user completes onboarding —
    // capture the booker selection here and let `useOnboardingCompletion`
    // submit it on Get Started, so a user who bails out mid-flow doesn't end
    // up with a phantom calendar invite.
    const bookingIntent = meetingBookerRef.current?.getBookingIntent() ?? null;
    updateFormData('bookingIntent', bookingIntent);
    handleContinue();
  };

  // HubSpot's SDR form 400s without a valid phone + reason, so the clinic
  // step's Continue button stays disabled until both are valid when
  // request-demo (or the webinar booked-out fallback) is opted in. Mirrors
  // `CompletionStep`'s identical gate.
  const webinarBookedOut = useAtomValue(webinarBookedOutAtom);
  const requestDemoBlocked = isRequestDemoBlocked(experience, formData, webinarBookedOut);

  return (
    <PersonalizationLayout>
      {currentStep === 'about-you' && (
        <AboutYouStep
          firstName={formData.firstName}
          lastName={formData.lastName}
          specialty={formData.specialty}
          otherSpecialty={formData.otherSpecialty}
          role={formData.role}
          country={formData.country}
          jurisdiction={formData.jurisdiction}
          displayLanguage={formData.displayLanguage}
          availableDisplayLanguages={availableDisplayLanguages}
          showLanguageDropdown={showLanguageDropdown}
          phoneNumber={formData.phoneNumber}
          onFirstNameChange={(value) => updateFormData('firstName', value)}
          onLastNameChange={(value) => updateFormData('lastName', value)}
          onSpecialtyChange={(value) => updateFormData('specialty', value)}
          onOtherSpecialtyChange={(value) => updateFormData('otherSpecialty', value)}
          onRoleChange={(value) => updateFormData('role', value)}
          onCountryChange={handleCountryChange}
          onJurisdictionChange={(value) => updateFormData('jurisdiction', value)}
          onDisplayLanguageChange={(v) => updateFormData('displayLanguage', v)}
          onPhoneNumberChange={(value) => updateFormData('phoneNumber', value)}
          onContinue={handleContinue}
          isValid={isCurrentStepValid}
        />
      )}
      {currentStep === 'clinic' && (
        <ClinicStep
          clinicName={formData.clinicName}
          country={formData.country}
          clinicSize={formData.clinicSize}
          emrEhr={formData.emrEhr}
          onClinicNameChange={(value) => updateFormData('clinicName', value)}
          onOfficialPracticeChange={(isOfficial, practiceId) => {
            updateFormData('isOfficialPractice', isOfficial);
            updateFormData('cqcPracticeId', practiceId);
          }}
          onClinicSizeChange={(value) => updateFormData('clinicSize', value)}
          onEmrEhrChange={(value) => updateFormData('emrEhr', value)}
          onContinue={handleClinicContinue}
          isValid={isCurrentStepValid && !requestDemoBlocked}
          hasPinnedFooter
          bookDemoSlot={
            <DemoOptInSection
              experience={experience}
              webinarOptIn={formData.ctaOptIn}
              onWebinarOptInChange={(value) => updateFormData('ctaOptIn', value)}
              demoReason={formData.demoReason}
              onDemoReasonChange={(value) => updateFormData('demoReason', value)}
              phoneNumber={formData.phoneNumber}
              onPhoneNumberChange={(value) => updateFormData('phoneNumber', value)}
              defaultCountry={formData.country?.countryCode as Country | undefined}
              countryName={formData.country?.name}
              firstName={formData.firstName}
              lastName={formData.lastName}
              email={ctx.userEmail}
              companyName={formData.clinicName}
              meetingBookerRef={meetingBookerRef}
            />
          }
        />
      )}
      {(currentStep === 'invite-team' || currentStep === 'tour-invite-team') &&
        formData.clinicSize && <InviteTeamStep />}
      {currentStep === 'mic-test' && isGuidedTour && (
        <TourTranscriptionStep
          onTranscriptChange={micTest.setTranscript}
          onRecordingDurationChange={micTest.setRecordingDurationSec}
          sampleText={micSampleText}
          specialty={localizedSpecialty}
          requireRecordingToContinue={!micTest.hasCompleted}
          onContinue={handleMicTestContinue}
          onSkip={handleMicTestSkip}
        />
      )}
      {currentStep === 'mic-test' && !isGuidedTour && (
        <MicTestStepComponent
          onTranscriptChange={micTest.setTranscript}
          onRecordingDurationChange={micTest.setRecordingDurationSec}
          sampleText={micSampleText}
          requireRecordingToContinue={!micTest.hasCompleted}
          onContinue={handleMicTestContinue}
          onSkip={handleMicTestSkip}
        />
      )}
      {currentStep === 'note-ready' && (
        <div className="flex h-full flex-col gap-6">
          <div>
            <TypographyH3 className="mb-2">
              <FormattedMessage
                defaultMessage="Your first note is ready"
                id="JNa3J5xM6F"
                description="Heading of the onboarding step shown once the demo note has generated"
              />
            </TypographyH3>
            <TypographyP className="text-text-secondary">
              <FormattedMessage
                defaultMessage="This is what Heidi does — turns conversations into structured clinical notes."
                id="BlA9D9Cv3/"
                description="Subtitle under the first-note-ready onboarding heading"
              />
            </TypographyP>
            {showComplianceCopy && (
              <TypographyP className="mt-4 text-text-secondary">
                <FormattedMessage
                  defaultMessage="Ensure you document nonverbal cues (e.g. patient nods head for consent). When reviewing a note be sure to verify its accuracy."
                  id="JBQZXeh9YX"
                  description="Compliance reminder shown under the first-note-ready onboarding heading"
                />
              </TypographyP>
            )}
          </div>
          {micTest.phase === 'finished' && (
            <m.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <Button
                size="lg"
                onClick={() => {
                  // `note-ready` has `showInProgress: false`, so
                  // `handleContinue`'s default `trackStepCompleted` is
                  // skipped — fire it explicitly here so the funnel sees
                  // the user advancing past this step.
                  analytics.trackStepCompleted('note-ready', currentStepIndex + 1);
                  micTest.setPhase('editing');
                  goToNextStep();
                }}
              >
                <FormattedMessage
                  defaultMessage="Continue"
                  id="fJXEmXw76+"
                  description="Button advancing past the first-note-ready onboarding step"
                />
              </Button>
            </m.div>
          )}
        </div>
      )}
      {currentStep === 'note-edit' && (
        <LearnBasicsSteppedStep
          onContinue={handleNoteEditContinue}
          onSkip={handleNoteEditSkip}
        />
      )}
      {isGuidedTourStep(currentStep) && <GuidedTourSteps step={currentStep} />}
      {currentStep === 'completion' && (
        <PrivacyStep
          onGetStarted={completion.onGetStarted}
          isSubmitting={completion.isCompletingOnboarding}
          countryCode={formData.country?.countryCode}
          isGuidedTour={isGuidedTour}
        />
      )}
    </PersonalizationLayout>
  );
}
